/**
 * Browser-side types and fetchers for the two content feeds.
 *
 * Shapes mirror `functions/_lib/content.ts` exactly — that module owns
 * validation on the way in, this one owns the read model on the way out.
 */

export type PortfolioItem = {
  id: number;
  title: string;
  category: string;
  description: string;
  imageKey: string;
  imageUrl: string;
  /** Resolved URL to load; already falls back to the R2 key. */
  src: string;
  alt: string;
  /** Intrinsic size, or null when the row predates dimension capture. */
  width: number | null;
  height: number | null;
  sortOrder: number;
  published: boolean;
  createdAt: string;
  updatedAt: string;
};

export type Ad = {
  id: number;
  headline: string;
  kicker: string;
  body: string;
  imageKey: string;
  imageUrl: string;
  src: string;
  ctaLabel: string;
  ctaUrl: string;
  /** YYYY-MM-DD, or null for open-ended. */
  startsAt: string | null;
  endsAt: string | null;
  width: number | null;
  height: number | null;
  sortOrder: number;
  published: boolean;
  createdAt: string;
  updatedAt: string;
};

/** Success carries the payload; failure carries a reason. Never both, never neither. */
type Result<T> = ({ ok: true } & T) | { ok: false; error: string; fields?: Record<string, string> };

/**
 * The response is untrusted, so it is validated from `unknown` rather than
 * asserted into `Result<T>`. A body that is not JSON, or a proxy returning
 * HTML, must not become a typed success.
 */
function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null ? (value as Record<string, unknown>) : null;
}

function isSuccess(value: unknown): boolean {
  return asRecord(value)?.ok === true;
}

function reasonOf(value: unknown): string | undefined {
  const reason = asRecord(value)?.error;
  return typeof reason === "string" && reason ? reason : undefined;
}

function fieldsOf(value: unknown): Record<string, string> | undefined {
  const fields = asRecord(value)?.fields;
  if (typeof fields !== "object" || fields === null) return undefined;
  const out: Record<string, string> = {};
  for (const [key, message] of Object.entries(fields)) {
    if (typeof message === "string") out[key] = message;
  }
  return out;
}

async function readJson(response: Response): Promise<unknown> {
  return response.json().catch(() => null);
}

async function get<T>(url: string): Promise<Result<T>> {
  try {
    const response = await fetch(url, { headers: { Accept: "application/json" } });
    const payload = await readJson(response);
    if (!response.ok || !isSuccess(payload)) {
      return { ok: false, error: reasonOf(payload) ?? "Something went wrong." };
    }
    return payload as { ok: true } & T;
  } catch {
    return { ok: false, error: "Couldn't reach the server." };
  }
}

export function fetchPortfolio() {
  return get<{ items: PortfolioItem[]; categories: string[] }>("/api/portfolio");
}

export function fetchAds() {
  return get<{ items: Ad[]; today: string }>("/api/ads");
}

/* ------------------------------------------------------------- admin calls */

export function fetchAdminPortfolio() {
  return get<{ items: PortfolioItem[] }>("/api/admin/portfolio");
}

export function fetchAdminAds() {
  return get<{ items: Ad[] }>("/api/admin/ads");
}



async function send<T>(
  url: string,
  method: "POST" | "PATCH" | "DELETE",
  body: unknown,
): Promise<Result<T>> {
  try {
    const response = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body ?? {}),
    });
    const payload = await readJson(response);
    if (!response.ok || !isSuccess(payload)) {
      return {
        ok: false,
        error: reasonOf(payload) ?? "Something went wrong.",
        fields: fieldsOf(payload),
      };
    }
    return payload as { ok: true } & T;
  } catch {
    return { ok: false, error: "Couldn't reach the server." };
  }
}

export const createPortfolioItem = (body: unknown) =>
  send<{ item: PortfolioItem }>("/api/admin/portfolio", "POST", body);

export const updatePortfolioItem = (body: unknown) =>
  send<{ item: PortfolioItem }>("/api/admin/portfolio", "PATCH", body);

export const deletePortfolioItem = (id: number) =>
  send<Record<string, never>>("/api/admin/portfolio", "DELETE", { id });

export const createAd = (body: unknown) => send<{ item: Ad }>("/api/admin/ads", "POST", body);

export const updateAd = (body: unknown) => send<{ item: Ad }>("/api/admin/ads", "PATCH", body);

export const deleteAd = (id: number) => send<Record<string, never>>("/api/admin/ads", "DELETE", { id });

export type UploadResult = {
  ok: boolean;
  error?: string;
  needsMedia?: boolean;
  key?: string;
  url?: string;
};

/**
 * Uploads one image for the admin content forms.
 *
 * Resolves rather than rejects on every failure so callers can render the
 * reason inline. A `needsMedia` result means R2 is not connected yet and the
 * form should fall back to the URL field.
 */
export async function uploadImage(file: File, folder: string): Promise<UploadResult> {
  try {
    const form = new FormData();
    form.append("file", file);
    form.append("folder", folder);
    const response = await fetch("/api/admin/upload", { method: "POST", body: form });
    const payload = await readJson(response);
    if (!response.ok || !isSuccess(payload)) {
      return {
        ok: false,
        error: reasonOf(payload) ?? "Upload failed.",
        needsMedia: asRecord(payload)?.needsMedia === true,
      };
    }
    return payload as UploadResult;
  } catch {
    return { ok: false, error: "Couldn't reach the server." };
  }
}
